/**
 * Project Impact Assessment — Structured Project Credibility Engine
 * Route: /project/assessment (new) | /project/assessment/:id (existing)
 *
 * 10-step engine to turn a project into a quantified, credible,
 * and financeable climate asset.
 *
 * Design: "Bloomberg Terminal for Carbon" — data-dense, analytical, rigorous.
 * NOT a guided journey. NOT promotional. Lead with numbers.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, ReferenceLine
} from "recharts";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  Settings, Activity, TrendingDown, Shield, CheckSquare, GitBranch,
  DollarSign, AlertCircle, FileText, Save, Download, ArrowRight,
  ChevronRight, RefreshCw, CheckCircle2, X, Info, Layers, BarChart2,
  Target, Zap, Clock, Award
} from "lucide-react";
import { SDGFullPanel } from "@/components/SDGPanel";

const BRAND = "#0F172A";
const TEAL  = "#059669";

// ── Step definitions ───────────────────────────────────────────────────────
const STEPS = [
  { id: 1, key: "setup",         label: "Project Setup",            icon: Settings     },
  { id: 2, key: "baseline",      label: "Baseline Scenario",        icon: TrendingDown },
  { id: 3, key: "project",       label: "Project Scenario",         icon: Activity     },
  { id: 4, key: "impact",        label: "Impact Over Time",         icon: BarChart2    },
  { id: 5, key: "mrv",           label: "Data Quality & MRV",       icon: Shield       },
  { id: 6, key: "additionality", label: "Additionality & Integrity",icon: CheckSquare  },
  { id: 7, key: "pathway",       label: "Carbon Value & Pathway",   icon: GitBranch    },
  { id: 8, key: "financial",     label: "Investment & Financial",   icon: DollarSign   },
  { id: 9, key: "readiness",     label: "Readiness Decision",       icon: Target       },
  { id: 10,key: "outputs",       label: "Outputs & Next Actions",   icon: FileText     },
];

import { SDGSummaryRow } from "@/components/SDGPanel";

// ── Per-type input configuration (mirrors backend PROJECT_TYPE_CONFIG) ─────
const TYPE_CONFIG = {
  solar:             { baseline: ["electricity_kwh","diesel_liters","grid_factor_source"], project: ["solar_fraction_pct"], default_rf: 70, rf_label: "Solar fraction (% of electricity replaced)", note: "Solar fraction × electricity baseline Scope 2. Diesel backup remains." },
  energy_efficiency: { baseline: ["electricity_kwh","diesel_liters","coal_kg","grid_factor_source"], project: ["reduction_factor_pct"], default_rf: 25, rf_label: "Efficiency gain vs baseline (%)", note: "Applied proportionally across all emission sources." },
  fuel_switch:       { baseline: ["diesel_liters","petrol_liters","grid_factor_source"], project: ["switch_fraction_pct"], default_rf: 40, rf_label: "Fraction of diesel switched to LPG (%)", note: "Diesel (2.68) → LPG (1.51 kgCO₂e/L). Net reduction ≈40%." },
  waste_recovery:    { baseline: ["waste_kg"], project: ["methane_capture_pct"], default_rf: 75, rf_label: "Methane capture efficiency (%)", note: "Baseline: landfill (0.58 kgCO₂e/kg). Project: combustion (0.05). IPCC 2006." },
  circular_recycling:{ baseline: ["waste_kg","other_tco2e"], project: ["recycling_rate_pct","avoided_production_tco2e"], default_rf: 60, rf_label: "Recycling/diversion rate (%)", note: "Reduction from landfill diversion + avoided production. Material-specific." },
  agriculture_land_use:{ baseline: ["land_area_ha","fertilizer_kg"], project: ["soil_sequestration_tco2e_per_ha","land_area_ha"], default_rf: 0, rf_label: "Estimated sequestration (tCO₂e/ha/yr)", note: "Default: 0.5 tCO₂e/ha/yr (conservative). Range: 0.2–3.0. Site assessment required." },
  industrial_process:{ baseline: ["diesel_liters","electricity_kwh","coal_kg","other_tco2e","grid_factor_source"], project: ["reduction_factor_pct"], default_rf: 30, rf_label: "Process emission reduction (%)", note: "Applied uniformly. Document process-specific basis separately." },
  other:             { baseline: ["diesel_liters","electricity_kwh","other_tco2e","grid_factor_source"], project: ["reduction_factor_pct"], default_rf: 40, rf_label: "Estimated emission reduction (%)", note: "User-specified. Document the basis for this assumption." },
  // DPCS-H v1.0 — Low-Carbon Housing
  housing_low_carbon:{ baseline: [], project: [], default_rf: 30, rf_label: "Combined emission reduction vs conventional unit (%)", note: "DPCS-H v1.0: embodied carbon (amortized 30yr) + operational. Per-unit model." },
};

const PROJECT_TYPES = [
  { value: "solar",                label: "Solar / Renewable Energy"          },
  { value: "waste_recovery",       label: "Waste Recovery / Biogas"           },
  { value: "energy_efficiency",    label: "Energy Efficiency"                 },
  { value: "fuel_switch",          label: "Fuel Switching"                    },
  { value: "transport",            label: "Transport & Fleet Decarbonization" },
  { value: "industrial_process",   label: "Industrial Process"                },
  { value: "agriculture_land_use", label: "Agriculture / Land Use"            },
  { value: "housing_low_carbon",   label: "Low-Carbon Housing (DPCS-H v1.0)" },
  { value: "circular_recycling",   label: "Circularity / Recycling"           },
  { value: "low_carbon_materials", label: "Low-Carbon Materials"              },
  { value: "other",                label: "Other"                             },
];

const STAGES = [
  { value: "concept",        label: "Concept / Pre-feasibility" },
  { value: "development",    label: "Development / Design"      },
  { value: "implementation", label: "Implementation"            },
  { value: "operation",      label: "Operational"               },
];

const READINESS_CFG = {
  not_ready:          { label: "Not Ready",           color: "text-red-600",     bg: "bg-red-50 border-red-200",     dot: "bg-red-500"     },
  structuring_needed: { label: "Structuring Needed",  color: "text-amber-700",   bg: "bg-amber-50 border-amber-200", dot: "bg-amber-500"   },
  ready_for_financing:{ label: "Ready for Financing", color: "text-teal-700",    bg: "bg-teal-50 border-teal-200",   dot: "bg-teal-500"    },
  ready_for_project:  { label: "Ready for Project",   color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  ready_for_pathway:  { label: "Ready for Pathway",   color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500 animate-pulse" },
};

const SCORE_COLORS = {
  strong:   "text-emerald-600 bg-emerald-50",
  moderate: "text-amber-600 bg-amber-50",
  weak:     "text-red-600 bg-red-50",
};

// ── Shared form components ─────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">{label}</label>
    {hint && <p className="text-xs text-slate-400 mb-1.5">{hint}</p>}
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", testid }) => (
  <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 bg-white font-mono" />
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value || ""} onChange={e => onChange(e.target.value)}
    data-testid={testid}
    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 bg-white">
    <option value="">Select...</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const SaveBtn = ({ onClick, busy, label = "Save & Continue" }) => (
  <button onClick={onClick} disabled={busy}
    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-all"
    data-testid="step-save-btn">
    {busy ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
    {busy ? "Saving..." : label}
  </button>
);

// ── Step Rail ─────────────────────────────────────────────────────────────
function StepRail({ activeStep, summary, onStepClick }) {
  const completedKeys = [
    summary?.baseline_tco2e   && "baseline",
    summary?.annual_reduction && "project",
    summary?.annual_reduction && "impact",
    summary?.mrv_readiness    && "mrv",
    summary?.additionality_score && "additionality",
    summary?.annual_reduction && "pathway",
    summary?.capex_usd        && "financial",
    summary?.readiness_state  && "readiness",
  ].filter(Boolean);

  return (
    <div className="w-[220px] flex-shrink-0 bg-[#0B1220] border-r border-white/8 h-full overflow-y-auto sticky top-14"
      data-testid="project-step-rail">
      <div className="p-4">
        <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-3 px-2">Assessment Steps</p>
        {STEPS.map((s, i) => {
          const done   = completedKeys.includes(s.key);
          const active = activeStep === s.key;
          const Icon   = s.icon;
          return (
            <button key={s.key}
              onClick={() => onStepClick(s.key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium text-left transition-all mb-0.5 ${
                active ? "bg-teal-600/20 text-teal-300 border border-teal-600/30" :
                done   ? "text-emerald-400 hover:bg-white/5" :
                         "text-slate-500 hover:bg-white/5 hover:text-slate-300"
              }`}
              data-testid={`step-nav-${s.key}`}>
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                active ? "bg-teal-600/30" : done ? "bg-emerald-900/30" : "bg-white/5"
              }`}>
                {done
                  ? <CheckCircle2 size={11} className="text-emerald-400" />
                  : <Icon size={11} className={active ? "text-teal-300" : "text-slate-500"} />
                }
              </div>
              <span className="truncate">{s.id}. {s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Right Insight Panel ────────────────────────────────────────────────────
function InsightPanel({ summary, sdgData }) {
  if (!summary) return null;
  const ready = READINESS_CFG[summary.readiness_state] || READINESS_CFG.not_ready;
  const SCORE_TAG = (label) => {
    if (!label) return "—";
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${SCORE_COLORS[label] || "text-slate-400 bg-slate-100"}`}>{label}</span>;
  };

  const MetricRow = ({ label, value, sub, mono = false }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-white/5">
      <span className="text-xs text-slate-400">{label}</span>
      <div className="text-right">
        {value !== null && value !== undefined
          ? <span className={`text-xs font-bold text-white ${mono ? "font-mono" : ""}`}>{value}</span>
          : <span className="text-xs text-slate-600">—</span>
        }
        {sub && <p className="text-xs text-slate-600">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="w-[280px] flex-shrink-0 bg-[#0B1220] border-l border-white/8 h-full overflow-y-auto sticky top-14 hidden xl:block"
      data-testid="project-insight-panel">
      <div className="p-4 space-y-4">
        <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Project Metrics</p>

        {/* Readiness badge */}
        <div className={`rounded-lg px-3 py-2.5 border ${ready.bg}`}>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${ready.dot}`} />
            <p className={`text-xs font-bold ${ready.color}`}>{ready.label}</p>
          </div>
        </div>

        {/* Core impact metrics */}
        <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Emissions Impact</p>
          <MetricRow label="Baseline"   value={summary.baseline_tco2e   ? `${summary.baseline_tco2e}t`   : null} sub="tCO₂e/yr" />
          <MetricRow label="Project"    value={summary.project_tco2e    ? `${summary.project_tco2e}t`    : null} sub="tCO₂e/yr" />
          <MetricRow label="Reduction"  value={summary.annual_reduction ? `${summary.annual_reduction}t` : null} sub="tCO₂e/yr" />
          <MetricRow label="Lifetime"   value={summary.lifetime_reduction ? `${summary.lifetime_reduction}t` : null} sub={`tCO₂e / ${summary.project_life_years || "—"}yr`} />
        </div>

        {/* Carbon value */}
        {summary.vcm_value_low && (
          <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Carbon Value (indicative)</p>
            <MetricRow label="Annual VCM" value={`$${Math.round(summary.vcm_value_low/1000)}k–$${Math.round(summary.vcm_value_high/1000)}k`} />
          </div>
        )}

        {/* Credibility scores */}
        <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Credibility Signals</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">MRV Readiness</span>
              {SCORE_TAG(summary.mrv_readiness)}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Additionality</span>
              {SCORE_TAG(summary.additionality_score)}
            </div>
          </div>
        </div>

        {/* SDG Attribution Summary */}
        {sdgData?.attributed_sdgs?.length > 0 && (
          <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">SDG Contributions</p>
            <div className="flex flex-wrap gap-1.5">
              {sdgData.attributed_sdgs.map(a => (
                <div key={a.sdg} className="flex items-center gap-1 px-2 py-1 rounded-lg"
                  style={{ backgroundColor: a.color + "20" }}>
                  <span className="text-xs font-bold text-white">SDG {a.sdg}</span>
                </div>
              ))}
            </div>
            {sdgData.lifetime_cars_equivalent > 0 && (
              <p className="text-xs text-slate-500 mt-2">≈ {sdgData.lifetime_cars_equivalent.toLocaleString()} car-years of lifetime impact</p>
            )}
          </div>
        )}

        {/* Financial + Cost/tCO₂e Benchmark */}
        {summary.capex_usd && (
          <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Financial</p>
            <MetricRow label="CAPEX"         value={`$${summary.capex_usd.toLocaleString()}`} />
            <MetricRow label="Savings/yr"    value={summary.annual_savings_usd ? `$${Math.round(summary.annual_savings_usd).toLocaleString()}` : null} />
            <MetricRow label="Payback"       value={summary.payback_years ? `${summary.payback_years} yrs` : null} />
            {summary.cost_per_tco2e && (
              <div className="pt-2 mt-2 border-t border-white/5" data-testid="cost-per-tco2e-benchmark">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">Cost / tCO₂e</span>
                  <span className="text-xs font-bold text-white font-mono">~${Math.round(parseFloat(summary.cost_per_tco2e)*0.85)}–{Math.round(parseFloat(summary.cost_per_tco2e)*1.15)}/t*</span>
                </div>
                {/* Benchmark bar */}
                {(() => {
                  const cost = parseFloat(summary.cost_per_tco2e) || 0;
                  const BM_LOW = 10, BM_HIGH = 50;
                  const position = cost < BM_LOW  ? "below" :
                                   cost <= BM_HIGH ? "within" : "above";
                  const posStyle = {
                    below:  { text: "text-emerald-400", label: "Below typical range" },
                    within: { text: "text-teal-400",    label: "Within typical range" },
                    above:  { text: "text-amber-400",   label: "Above typical range"  },
                  }[position];
                  // Visual marker position on bar (0-100%)
                  const pct = Math.min(100, Math.max(0, ((cost - 0) / BM_HIGH) * 100));
                  return (
                    <>
                      <div className="relative h-2 bg-white/10 rounded-full overflow-hidden mb-1">
                        {/* Benchmark zone */}
                        <div className="absolute h-full bg-teal-500/30 rounded-full"
                          style={{ left: `${(BM_LOW/BM_HIGH)*100}%`, width: `${100-(BM_LOW/BM_HIGH)*100}%` }} />
                        {/* Project position dot */}
                        <div className="absolute top-0 w-2 h-2 rounded-full bg-white border border-slate-600 -translate-x-1"
                          style={{ left: `${Math.min(98, pct)}%` }} />
                      </div>
                      <div className="flex justify-between text-xs text-slate-600 mb-1">
                        <span>$0</span>
                        <span className="text-slate-500">${BM_LOW}–${BM_HIGH} typical</span>
                        <span>$50+</span>
                      </div>
                      <p className={`text-xs font-semibold ${posStyle.text}`}>{posStyle.label}</p>
                    </>
                  );
                })()}
                <p className="text-xs text-slate-600 italic mt-1">
                  Indicative benchmark only. Actual project economics vary by region, methodology, and market conditions.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step Canvas Wrapper ────────────────────────────────────────────────────
function StepCard({ number, icon: Icon, title, description, children, footer }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5"
      data-testid={`step-card-${number}`}>
      <div className="px-6 py-4 border-b border-slate-100 flex items-start gap-4 bg-slate-50/50">
        <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-teal-600" />
        </div>
        <div>
          <p className="text-xs text-teal-500 font-bold uppercase tracking-widest">Step {number}</p>
          <h2 className="text-sm font-bold text-slate-900 leading-snug">{title}</h2>
          {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
      {footer && <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center gap-3">{footer}</div>}
    </div>
  );
}

// ── STEP 1: Project Setup ──────────────────────────────────────────────────
function Step1_Setup({ pia, onSaved }) {
  const [form, setForm] = useState({
    name: pia?.name || "", country: pia?.country || "",
    sector: pia?.sector || "", project_type: pia?.project_type || "",
    stage: pia?.stage || "concept", site_location: pia?.site_location || "",
    start_date: pia?.start_date || "", project_life_years: pia?.project_life_years || 10,
    description: pia?.description || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { toast.warning("Project name is required."); return; }
    setBusy(true);
    try {
      const body = { ...form, project_life_years: parseInt(form.project_life_years) || 10 };
      let res;
      if (pia?.id) {
        res = await client.patch(`/project-assessment/${pia.id}/setup`, body);
      } else {
        res = await client.post("/project-assessment/", body);
      }
      toast.success("Project setup saved.");
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  return (
    <StepCard number={1} icon={Settings} title="Project Setup"
      description="Define the project clearly. This determines pathway classification and benchmark context."
      footer={<SaveBtn onClick={handleSave} busy={busy} />}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Project Name">
            <Input value={form.name} onChange={v => set("name", v)} placeholder="e.g. Ikeja Solar Hybrid Installation" testid="pia-name" />
          </Field>
        </div>
        <Field label="Country">
          <Select value={form.country} onChange={v => set("country", v)} testid="pia-country"
            options={["Nigeria","Ghana","South Africa","Kenya","Ethiopia","Tanzania","Other"].map(c => ({value:c,label:c}))} />
        </Field>
        <Field label="Sector">
          <Select value={form.sector} onChange={v => set("sector", v)} testid="pia-sector"
            options={["Energy","Manufacturing","Transport","Agriculture","Waste","Buildings","Other"].map(s=>({value:s,label:s}))} />
        </Field>
        <Field label="Project Type">
          <Select value={form.project_type} onChange={v => set("project_type", v)} testid="pia-type"
            options={PROJECT_TYPES} />
        </Field>
        <Field label="Project Stage">
          <Select value={form.stage} onChange={v => set("stage", v)} testid="pia-stage"
            options={STAGES} />
        </Field>
        <Field label="Site / Location">
          <Input value={form.site_location} onChange={v => set("site_location", v)} placeholder="e.g. Ikeja Industrial Estate, Lagos" testid="pia-location" />
        </Field>
        <Field label="Project Life (years)">
          <Input value={form.project_life_years} onChange={v => set("project_life_years", v)} type="number" placeholder="10" testid="pia-life" />
        </Field>
        <div className="col-span-2">
          <Field label="Description (optional)" hint="Brief project summary">
            <textarea value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="Describe the project intervention and expected impact..."
              rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-500 bg-white" />
          </Field>
        </div>
      </div>
    </StepCard>
  );
}


// ── STEP 2: Baseline Scenario (type-aware) ────────────────────────────────
const BASELINE_FIELD_CONFIG = {
  waste_recovery: {
    headline: "Waste Handling System (Before Project)",
    hint:     "Define the waste stream and current disposal method. This is the baseline against which methane avoidance is measured.",
    primary: [
      { key: "waste_kg", label: "Waste Input — Organic/Agricultural/Municipal", unit: "kg/yr",
        hint: "Total waste entering the disposal/treatment system annually. E.g. 5,000 tonnes = 5,000,000 kg." },
    ],
    secondary: [
      { key: "production_volume", label: "Production Volume (optional)", unit: "units/yr",
        hint: "E.g. tonnes of crops, animals processed — for intensity calculation" },
    ],
    tech_placeholder: "e.g. Open dump / unmanaged landfill / agricultural field burning",
    note_placeholder: "Document waste composition, moisture content, and disposal method. Key for additionality and baseline credibility.",
  },
  solar: {
    headline: "Existing Energy Consumption (Before Solar)",
    hint:     "Define current electricity and diesel consumption that the solar system will partially replace.",
    primary: [
      { key: "electricity_kwh", label: "Annual Grid Electricity", unit: "kWh/yr",
        hint: "Total electricity consumed from the national grid annually" },
      { key: "diesel_liters",  label: "Diesel / Generator Backup", unit: "L/yr",
        hint: "Diesel consumed for generation backup — will be partially displaced" },
    ],
    secondary: [],
    tech_placeholder: "e.g. Grid-connected with diesel backup generators",
    note_placeholder: "Document grid reliability, typical outage hours, and current electricity tariff.",
  },
  fuel_switch: {
    headline: "Current Fuel Consumption (Before Switch)",
    hint:     "Define diesel/petrol volumes being replaced by lower-emission alternatives.",
    primary: [
      { key: "diesel_liters", label: "Annual Diesel Consumption", unit: "L/yr",
        hint: "Total diesel consumed by fleet, equipment, or processes — to be switched" },
      { key: "petrol_liters", label: "Annual Petrol Consumption (optional)", unit: "L/yr",
        hint: "Total petrol consumed, if applicable" },
    ],
    secondary: [],
    tech_placeholder: "e.g. Diesel fleet + diesel-powered industrial equipment",
    note_placeholder: "Document vehicle count, operating hours, and fuel purchase records.",
  },
  energy_efficiency: {
    headline: "Current Energy Use (Before Improvements)",
    hint:     "Capture all energy sources that will be reduced by the efficiency intervention.",
    primary: [
      { key: "electricity_kwh", label: "Annual Electricity", unit: "kWh/yr", hint: "Grid electricity consumed annually" },
      { key: "diesel_liters",   label: "Diesel / Fuel Oil", unit: "L/yr",   hint: "Diesel or heavy fuel oil consumed" },
      { key: "coal_kg",         label: "Coal (if applicable)", unit: "kg/yr", hint: "Coal combusted for process or heating" },
    ],
    secondary: [],
    tech_placeholder: "e.g. Inefficient boilers / uninsulated buildings / oversized motors",
    note_placeholder: "Reference energy audit or consumption bills underpinning the baseline.",
  },
  agriculture_land_use: {
    headline: "Agricultural Land — Current State",
    hint:     "Define the land area and current management practices before the project intervention.",
    primary: [
      { key: "land_area_ha",  label: "Total Land Area", unit: "ha",
        hint: "Hectares under the project boundary (agricultural land, pasture, or degraded land)" },
      { key: "fertilizer_kg", label: "Annual Fertilizer Use (optional)", unit: "kg N/yr",
        hint: "Nitrogen fertilizer applied per year — contributes N₂O emissions" },
    ],
    secondary: [],
    tech_placeholder: "e.g. Conventional tillage / degraded pasture / bare soil post-deforestation",
    note_placeholder: "Specify current soil carbon levels if known. Reference IPCC Tier 1 defaults if not measured.",
  },
  circular_recycling: {
    headline: "Waste Stream (Before Recycling / Diversion)",
    hint:     "Define the waste stream currently going to landfill or incineration.",
    primary: [
      { key: "waste_kg",     label: "Total Waste Generated", unit: "kg/yr",
        hint: "Total waste stream before any recycling or diversion" },
      { key: "other_tco2e",  label: "Additional Emissions (optional)", unit: "tCO₂e/yr",
        hint: "If avoided production or avoided landfill methane needs direct entry" },
    ],
    secondary: [],
    tech_placeholder: "e.g. Single-use plastics to landfill / organic waste to open dump",
    note_placeholder: "Document waste composition by material type (plastics, organics, metals, paper).",
  },
  // DPCS-H v1.0 — housing has its own dedicated Step 2 component
  housing_low_carbon: null,
};

const DEFAULT_FIELDS = {
  headline: "Current State (Before Project)",
  hint:     "Define existing energy, fuel, and process inputs that the project will reduce.",
  primary: [
    { key: "diesel_liters",   label: "Diesel Consumption", unit: "L/yr",   hint: "Annual diesel combustion" },
    { key: "electricity_kwh", label: "Grid Electricity",   unit: "kWh/yr", hint: "Annual grid electricity" },
    { key: "coal_kg",         label: "Coal",               unit: "kg/yr",  hint: "Coal combustion (if applicable)" },
    { key: "other_tco2e",     label: "Other Emissions",    unit: "tCO₂e/yr", hint: "Direct entry for custom sources" },
  ],
  secondary: [],
  tech_placeholder: "Describe existing technology or process",
  note_placeholder: "Document key assumptions and data sources underpinning the baseline.",
};

// ── DPCS-H v1.0: Housing Baseline Step ────────────────────────────────────
function Step2_Housing_Baseline({ pia, onSaved }) {
  const [form, setForm] = useState(pia?.baseline_data || {
    floor_area_m2: 65, cement_kg: 4500, steel_kg: 800, masonry_kg: 12000,
    insulation_kg: 0, annual_energy_kwh: 3600, cooling_energy_kwh: 800,
    grid_factor_source: "nigeria", project_life_years: 30,
  });
  const [busy,    setBusy]    = useState(false);
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const GRID_OPTS = [
    {value:"nigeria",      label:"Nigeria (0.43 kgCO₂e/kWh)"},
    {value:"ghana",        label:"Ghana (0.40 kgCO₂e/kWh)"},
    {value:"south_africa", label:"South Africa (0.90 kgCO₂e/kWh)"},
    {value:"global",       label:"Global avg (0.49 kgCO₂e/kWh)"},
  ];

  const handlePreview = async () => {
    try {
      const r = await client.post("/housing/calculate", { baseline_data: form, project_data: {}, life_years: form.project_life_years || 30 });
      setPreview(r.data);
    } catch { toast.error("Preview failed."); }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/baseline`, form);
      toast.success(`Baseline: ${res.data.computed_baseline} tCO₂e/yr (DPCS-H v1.0 per unit)`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  const numField = (k, label, unit, hint, placeholder) => (
    <div key={k} className="flex items-center gap-4 bg-slate-50 rounded-xl px-4 py-3">
      <div className="flex-1">
        <p className="text-xs font-bold text-slate-900">{label}</p>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <input type="number" min="0" value={form[k] || ""}
          onChange={e => set(k, parseFloat(e.target.value) || 0)}
          placeholder={placeholder || "0"}
          className="w-28 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white font-mono"
          data-testid={`housing-baseline-${k}`} />
        <span className="text-xs text-slate-400 w-14">{unit}</span>
      </div>
    </div>
  );

  return (
    <StepCard number={2} icon={TrendingDown} title="Baseline Scenario — Conventional Unit"
      description="DPCS-H v1.0. Define a typical conventional housing unit. Per-unit model — scale using Program Builder."
      footer={<><SaveBtn onClick={handleSave} busy={busy} /><button onClick={handlePreview} className="px-4 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Preview calculation</button></>}>
      <div className="space-y-3">
        {/* Methodology badge */}
        <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5">
          <Info size={12} className="text-teal-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-teal-700">DPCS-H v1.0 — Low-Carbon Housing Methodology</p>
            <p className="text-xs text-teal-600">Embodied carbon (amortized over project life) + Operational carbon. Per unit. Ref: IPCC IPPU Ch 2, World Steel Assoc. 2022.</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-2">Unit Specification</p>
        {numField("floor_area_m2", "Floor Area per Unit", "m²", "Gross floor area of a single housing unit", "65")}

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-2">Construction Materials (Conventional Baseline)</p>
        <p className="text-xs text-slate-400 italic">Enter material quantities per unit. Baseline: OPC cement, BF-BOF steel, conventional masonry.</p>
        {numField("cement_kg",   "Cement (OPC)", "kg/unit", "Ordinary Portland Cement. Factor: 0.875 tCO₂e/t (IPCC 2006 IPPU)", "4500")}
        {numField("steel_kg",    "Steel", "kg/unit", "Conventional BF-BOF steel. Factor: 1.85 tCO₂e/t (World Steel Assoc. 2022)", "800")}
        {numField("masonry_kg",  "Masonry / Blocks", "kg/unit", "Conventional blocks/bricks. Factor: 0.12 tCO₂e/t (BRE ICE v3)", "12000")}
        {numField("insulation_kg","Insulation (optional)", "kg/unit", "Synthetic insulation (if used). Factor: 1.60 tCO₂e/t", "0")}

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-2">Operational Energy (Annual per Unit)</p>
        {numField("annual_energy_kwh", "Annual Electricity", "kWh/yr", "Grid electricity per unit per year (lighting, appliances)", "3600")}
        {numField("cooling_energy_kwh","Annual Cooling Energy", "kWh/yr", "Cooling load per unit per year (optional)", "800")}
        <Field label="Grid Emission Factor">
          <Select value={form.grid_factor_source || "nigeria"} onChange={v => set("grid_factor_source", v)}
            testid="housing-grid" options={GRID_OPTS} />
        </Field>

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-2">Project Parameters</p>
        {numField("project_life_years", "Project Life / Amortization Period", "years", "Embodied carbon is divided over this period. Default: 30 years.", "30")}

        {preview && (
          <div className="bg-[#0F172A] rounded-xl p-4 mt-2" data-testid="housing-baseline-preview">
            <p className="text-xs text-teal-400 font-bold uppercase mb-2">Per-Unit Baseline — DPCS-H v1.0</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="bg-slate-800/50 rounded-lg px-3 py-2.5">
                <p className="text-xs text-slate-400 uppercase font-bold mb-0.5">Upfront Embodied Carbon</p>
                <p className="text-lg font-bold text-white">{preview.baseline_upfront_embodied_tco2e?.toFixed(2)}</p>
                <p className="text-xs text-slate-400">tCO₂e / unit (one-time, at construction)</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg px-3 py-2.5">
                <p className="text-xs text-slate-400 uppercase font-bold mb-0.5">Annual Operational Carbon</p>
                <p className="text-lg font-bold text-amber-300">{(preview.baseline_operational_tco2e_yr * 1000).toFixed(0)} kg</p>
                <p className="text-xs text-slate-400">kgCO₂e / unit / year (recurring)</p>
              </div>
              {preview.baseline_embodied_intensity_kg_m2 && (
                <div>
                  <p className="text-xs text-slate-500">Embodied intensity</p>
                  <p className="text-xs font-bold text-slate-300">{preview.baseline_embodied_intensity_kg_m2} kgCO₂e/m²</p>
                </div>
              )}
              {preview.baseline_operational_intensity_kg_m2_yr && (
                <div>
                  <p className="text-xs text-slate-500">Operational intensity</p>
                  <p className="text-xs font-bold text-slate-300">{preview.baseline_operational_intensity_kg_m2_yr} kgCO₂e/m²/yr</p>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2.5 border-t border-white/10 pt-2 italic">
              Embodied and operational are kept separate — never mixed into a single annual figure.
              Uncertainty ±{preview.uncertainty_pct}%.
            </p>
          </div>
        )}
      </div>
    </StepCard>
  );
}


// ── DPCS-H v1.0: Housing Project Scenario Step ────────────────────────────
function Step3_Housing_Project({ pia, onSaved }) {
  const [form, setForm] = useState(pia?.project_data || {
    scm_substitution_pct: 40, steel_recycled_pct: 50,
    solar_fraction_pct: 0, insulation_improvement_pct: 20,
    other_efficiency_pct: 0, natural_insulation: false,
    incremental_cost_usd: 0,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [financeView, setFinanceView] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePreview = async () => {
    try {
      const baselineData = pia?.baseline_data || {};
      const r = await client.post("/housing/calculate", {
        baseline_data: baselineData,
        project_data: form,
        life_years: baselineData.project_life_years || 30,
      });
      setPreview(r.data);
    } catch { toast.error("Preview failed."); }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/project-scenario`, form);
      toast.success(`Low-carbon unit: ${res.data.project_emissions} tCO₂e/yr (vs ${res.data.baseline_emissions})`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  const sliderField = (k, label, pct, min, max, step, hint) => (
    <div key={k} className="bg-slate-50 rounded-xl px-4 py-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <div><p className="text-xs font-bold text-slate-900">{label}</p>{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>
        <span className="text-sm font-bold text-teal-700 w-12 text-right">{pct}%</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={pct}
        onChange={e => set(k, parseInt(e.target.value))}
        className="w-full accent-teal-600"
        data-testid={`housing-project-${k}`} />
      <div className="flex justify-between text-xs text-slate-400"><span>{min}%</span><span>{max}%</span></div>
    </div>
  );

  return (
    <StepCard number={3} icon={TrendingDown} title="Low-Carbon Unit — Project Scenario"
      description="Define the low-carbon interventions vs the conventional baseline. All inputs are per unit."
      footer={<><SaveBtn onClick={handleSave} busy={busy} /><button onClick={handlePreview} className="px-4 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Compare to baseline</button></>}>
      <div className="space-y-3">
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
          <Info size={12} className="text-slate-400 flex-shrink-0" />
          <p className="text-xs text-slate-500">Adjust each lever to reflect the low-carbon design specification. Preview compares per-unit emissions vs the conventional baseline.</p>
        </div>

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-1">Embodied Carbon Interventions</p>
        {sliderField("scm_substitution_pct", "SCM Cement Substitution", form.scm_substitution_pct||40, 0, 60, 5,
          "% of OPC replaced by supplementary cementitious materials (fly ash, slag, pozzolan). 40% = blended cement factor.")}
        {sliderField("steel_recycled_pct", "Recycled Steel Content", form.steel_recycled_pct||50, 0, 90, 10,
          "% recycled content in steel. Shifts EAF factor → lower embodied carbon.")}
        <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
          <input type="checkbox" id="nat_insul" checked={!!form.natural_insulation}
            onChange={e => set("natural_insulation", e.target.checked)}
            className="w-4 h-4 accent-teal-600"
            data-testid="housing-natural-insulation" />
          <label htmlFor="nat_insul" className="flex-1 cursor-pointer">
            <p className="text-xs font-bold text-slate-900">Use Natural / Mineral Insulation</p>
            <p className="text-xs text-slate-400">Switches from synthetic insulation (1.60 tCO₂e/t) to natural/mineral (0.50 tCO₂e/t)</p>
          </label>
        </div>

        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-1">Operational Energy Interventions</p>
        {sliderField("solar_fraction_pct", "Solar PV Fraction", form.solar_fraction_pct||0, 0, 90, 5,
          "% of electricity demand met by rooftop solar. Reduces grid electricity consumption.")}
        {sliderField("insulation_improvement_pct", "Insulation / Passive Design Improvement", form.insulation_improvement_pct||20, 0, 60, 5,
          "% reduction in energy demand from improved insulation, shading, or passive cooling design.")}
        {sliderField("other_efficiency_pct", "Other Efficiency Measures", form.other_efficiency_pct||0, 0, 30, 5,
          "% from LED lighting, efficient appliances, smart controls, etc.")}

        {/* Cost layer — incremental cost per unit */}
        <p className="text-xs text-slate-500 font-bold uppercase tracking-wide pt-1">Finance Layer</p>
        <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-xs font-bold text-slate-900">Incremental Cost per Unit (USD)</p>
            <p className="text-xs text-slate-400">Additional cost vs conventional unit. Enables cost/tCO₂e calculation.</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-slate-400">$</span>
            <input type="number" min="0" value={form.incremental_cost_usd || ""}
              onChange={e => set("incremental_cost_usd", parseFloat(e.target.value)||0)}
              placeholder="e.g. 3500"
              className="w-28 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white font-mono"
              data-testid="housing-incremental-cost" />
          </div>
        </div>

        {preview && (
          <div className="bg-[#0F172A] rounded-xl p-4 mt-2" data-testid="housing-project-preview">
            {/* Finance View / Technical View toggle */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-teal-400 font-bold uppercase">
                {financeView ? "Finance View" : "Technical View"} — Per Unit
              </p>
              <button onClick={() => setFinanceView(v => !v)}
                className="text-xs font-bold px-2.5 py-1 rounded-full border border-teal-600 text-teal-400 hover:bg-teal-900/40"
                data-testid="housing-finance-toggle">
                {financeView ? "Technical View" : "Finance View"}
              </button>
            </div>

            {financeView ? (
              /* ── FINANCE VIEW ─────────────────────────────────── */
              <div className="space-y-2">
                {[
                  ["Upfront Embodied Reduction", `${preview.embodied_reduction_tco2e?.toFixed(2)} tCO₂e/unit`, "text-blue-300", "One-time, at construction"],
                  ["Annual Operational Reduction", `${(preview.operational_reduction_tco2e_yr * 1000).toFixed(0)} kgCO₂e/yr/unit`, "text-teal-300", "Recurring, every year"],
                  ["Total Lifetime Reduction", `${preview.total_lifetime_reduction_tco2e?.toFixed(1)} tCO₂e/unit`, "text-emerald-300", `Over ${preview.project_life_years}yr project life`],
                  preview.cost_per_tco2e_lifetime && ["Cost per tCO₂e (lifetime)", `~$${Math.round(preview.cost_per_tco2e_lifetime*0.85)}-${Math.round(preview.cost_per_tco2e_lifetime*1.15)}/t*`, "text-amber-300", "Incremental CAPEX ÷ total lifetime reduction"],
                  preview.annual_energy_cost_savings_usd > 0 && ["Energy Cost Savings", `$${preview.annual_energy_cost_savings_usd}/yr`, "text-green-300", "Household savings → improves loan repayment capacity"],
                ].filter(Boolean).map(([l,v,c,sub]) => (
                  <div key={l} className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400">{l}</p>
                      <p className="text-xs text-slate-500 italic">{sub}</p>
                    </div>
                    <p className={`text-sm font-bold ${c} font-mono`}>{v}</p>
                  </div>
                ))}
                {!preview.cost_per_tco2e_lifetime && (
                  <p className="text-xs text-slate-500 italic">Enter incremental cost above to unlock cost/tCO₂e</p>
                )}
                {/* Benchmark positioning */}
                {preview.benchmark_position && (
                  <div className="border-t border-white/10 pt-2">
                    <p className="text-xs text-amber-300 font-semibold">{preview.benchmark_position}</p>
                  </div>
                )}
                {/* Credit signal */}
                {preview.credit_signal && (
                  <div className={`border-t border-white/10 pt-2 flex items-center gap-2`}>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      preview.credit_signal.signal === "STRONG" ? "bg-emerald-500 text-white" :
                      preview.credit_signal.signal === "MODERATE" ? "bg-amber-500 text-white" : "bg-red-500 text-white"
                    }`} data-testid="credit-signal-badge">
                      {preview.credit_signal.signal}
                    </span>
                    <p className="text-xs text-slate-300 flex-1">{preview.credit_signal.summary?.substring(0,80)}...</p>
                  </div>
                )}
                {/* Finance narrative */}
                {preview.finance_narrative && (
                  <div className="border-t border-white/10 pt-2">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wide mb-1">Finance Narrative</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{preview.finance_narrative}</p>
                  </div>
                )}
              </div>
            ) : (
              /* ── TECHNICAL VIEW ──────────────────────────────── */
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ["Baseline upfront embodied",  `${preview.baseline_upfront_embodied_tco2e?.toFixed(2)} tCO₂e`],
                    ["Project upfront embodied",   `${preview.project_upfront_embodied_tco2e?.toFixed(2)} tCO₂e`],
                    ["Baseline operational/yr",    `${(preview.baseline_operational_tco2e_yr * 1000).toFixed(0)} kgCO₂e`],
                    ["Project operational/yr",     `${(preview.project_operational_tco2e_yr * 1000).toFixed(0)} kgCO₂e`],
                  ].map(([l,v]) => (
                    <div key={l}><p className="text-xs text-slate-500">{l}</p><p className="text-xs font-bold text-white">{v}</p></div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-2 grid grid-cols-2 gap-4">
                  {[
                    ["Embodied reduction",   `${preview.embodied_reduction_tco2e?.toFixed(2)} tCO₂e (${preview.embodied_reduction_pct}%)`],
                    ["Operational reduction",`${(preview.operational_reduction_tco2e_yr * 1000).toFixed(0)} kgCO₂e/yr (${preview.operational_reduction_pct}%)`],
                  ].map(([l,v]) => (
                    <div key={l}><p className="text-xs text-slate-500">{l}</p><p className="text-xs font-bold text-teal-400">{v}</p></div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-2.5 border-t border-white/10 pt-2 italic">
              Embodied = upfront (one-time) · Operational = annual (recurring) · Never mixed.
              Uncertainty ±{preview.uncertainty_pct}%.
            </p>
          </div>
        )}
      </div>
    </StepCard>
  );
}




// ── STEP 2: Baseline Scenario (type-aware) ────────────────────────────────
function Step2_Baseline({ pia, onSaved }) {
  const fieldCfg = BASELINE_FIELD_CONFIG[pia?.project_type] || DEFAULT_FIELDS;
  const [form, setForm] = useState(pia?.baseline_data || {});
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ── Housing: dedicated DPCS-H baseline form ──────────────────────────────
  if (pia?.project_type === "housing_low_carbon") return <Step2_Housing_Baseline pia={pia} onSaved={onSaved} />;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const GRID_OPTS = [
    {value:"nigeria",      label:"Nigeria (0.43 kgCO₂e/kWh)"},
    {value:"ghana",        label:"Ghana (0.40 kgCO₂e/kWh)"},
    {value:"south_africa", label:"South Africa (0.90 kgCO₂e/kWh)"},
    {value:"global",       label:"Global average (0.49 kgCO₂e/kWh)"},
  ];

  const needsGridFactor = ["solar","energy_efficiency","industrial_process","other"].includes(pia?.project_type || "other");

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/baseline`, form);
      toast.success(`Baseline: ${res.data.computed_baseline} tCO₂e/yr`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  return (
    <StepCard number={2} icon={TrendingDown} title="Baseline Scenario"
      description={`${fieldCfg.headline} — ${fieldCfg.hint}`}
      footer={<>
        <SaveBtn onClick={handleSave} busy={busy} />
        {pia?.baseline_emissions_tco2e && (
          <div className="ml-4 bg-slate-100 rounded-lg px-4 py-2 flex items-center gap-2">
            <span className="text-xs text-slate-500">Baseline computed:</span>
            <span className="text-sm font-bold text-slate-900 font-mono">{pia.baseline_emissions_tco2e} tCO₂e/yr</span>
            <span className="text-xs text-slate-400 italic">· Factors: IPCC 2006 / GHG Protocol</span>
          </div>
        )}
      </>}>
      <div className="space-y-3">
        {/* Primary inputs for this project type */}
        {fieldCfg.primary.map(f => (
          <div key={f.key} className="flex items-center gap-4 bg-slate-50 rounded-xl px-4 py-3">
            <div className="flex-1">
              <p className="text-xs font-bold text-slate-900">{f.label}</p>
              <p className="text-xs text-slate-400">{f.hint}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input type="number" min="0" value={form[f.key] || ""}
                onChange={e => set(f.key, e.target.value)}
                className="w-32 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white font-mono"
                data-testid={`baseline-${f.key}`} />
              <span className="text-xs text-slate-400 w-14">{f.unit}</span>
            </div>
          </div>
        ))}

        {/* Grid factor — only when electricity is relevant */}
        {needsGridFactor && (
          <Field label="Grid Emission Factor">
            <Select value={form.grid_factor_source || "nigeria"} onChange={v => set("grid_factor_source", v)} testid="baseline-grid"
              options={GRID_OPTS} />
          </Field>
        )}

        {/* Advanced / secondary fields — collapsed by default */}
        {fieldCfg.secondary.length > 0 && (
          <div>
            <button onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mt-1">
              {showAdvanced ? "▲ Hide" : "▼ Show"} optional fields
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-2">
                {fieldCfg.secondary.map(f => (
                  <div key={f.key} className="flex items-center gap-4 bg-slate-50 rounded-xl px-4 py-3">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-600">{f.label}</p>
                      <p className="text-xs text-slate-400">{f.hint}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input type="number" min="0" value={form[f.key] || ""}
                        onChange={e => set(f.key, e.target.value)}
                        className="w-28 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white font-mono" />
                      <span className="text-xs text-slate-400 w-14">{f.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Existing technology and notes */}
        <Field label="Existing Technology / Process">
          <Input value={form.existing_technology} onChange={v => set("existing_technology", v)}
            placeholder={fieldCfg.tech_placeholder} testid="baseline-tech" />
        </Field>
        <Field label="Baseline Assumptions & Data Sources" hint="FI analysts and verifiers will ask: what data underpins this baseline?">
          <textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)}
            placeholder={fieldCfg.note_placeholder}
            rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-500" />
        </Field>
      </div>
    </StepCard>
  );
}

// ── STEP 3: Project Scenario (type-aware, editable assumptions) ────────────
function Step3_Project({ pia, onSaved }) {
  const typeCfg = TYPE_CONFIG[pia?.project_type] || TYPE_CONFIG.other;
  const [form, setForm] = useState({
    reduction_factor_pct:  typeCfg.default_rf,
    solar_fraction_pct:    typeCfg.default_rf,
    methane_capture_pct:   75,
    switch_fraction_pct:   40,
    soil_sequestration_tco2e_per_ha: 0.5,
    land_area_ha:          pia?.baseline_data?.land_area_ha || "",
    reduction_assumption_note: "",
    new_technology:        "",
    capacity:              "",
    ramp_up_years:         1,
    ...(pia?.project_data || {}),
  });
  const [busy, setBusy] = useState(false);

  // ── Housing: dedicated DPCS-H project scenario form ──────────────────────
  if (pia?.project_type === "housing_low_carbon") return <Step3_Housing_Project pia={pia} onSaved={onSaved} />;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!pia?.baseline_emissions_tco2e) {
    return (
      <StepCard number={3} icon={Activity} title="Project Scenario"
        description="Define the project intervention and compute emission reduction.">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-700">
          Complete Step 2 (Baseline Scenario) before defining the project scenario.
        </div>
      </StepCard>
    );
  }

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = { ...form, ramp_up_years: parseInt(form.ramp_up_years || 1) };
      const res = await client.post(`/project-assessment/${pia.id}/project-scenario`, payload);
      toast.success(`Reduction: ${res.data.annual_reduction}t/yr · ${res.data.assumption_summary?.slice(0,60)}...`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  return (
    <StepCard number={3} icon={Activity} title="Project Scenario"
      description={`Baseline: ${pia.baseline_emissions_tco2e}t/yr. Define the project intervention and compute the reduction.`}
      footer={<>
        <SaveBtn onClick={handleSave} busy={busy} />
        {pia?.emission_reduction_tco2e != null && (
          <div className="ml-4 flex items-center gap-4 bg-teal-50 border border-teal-200 rounded-lg px-4 py-2">
            <span className="text-xs text-teal-600">Reduction:</span>
            <span className="text-sm font-bold text-teal-700 font-mono">{pia.emission_reduction_tco2e}t/yr</span>
          </div>
        )}
      </>}>
      <div className="space-y-4">
        {/* Primary reduction parameter for this project type */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-start gap-2 mb-3">
            <Info size={13} className="text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-teal-700">{typeCfg.rf_label}</p>
              <p className="text-xs text-teal-600 mt-0.5">Assumption: {typeCfg.note}</p>
            </div>
          </div>
          {/* Type-specific primary input */}
          {pia?.project_type === "solar" && (
            <Field label="Solar fraction — % of electricity replaced by solar" hint="User-editable. Default assumes 70% displacement.">
              <Input value={form.solar_fraction_pct} onChange={v => set("solar_fraction_pct", v)}
                type="number" placeholder={String(typeCfg.default_rf)} testid="proj-solar-fraction" />
            </Field>
          )}
          {pia?.project_type === "waste_recovery" && (
            <Field label="Methane capture efficiency (%)" hint="% of waste methane captured and combusted. Default: 75%.">
              <Input value={form.methane_capture_pct} onChange={v => set("methane_capture_pct", v)}
                type="number" placeholder="75" testid="proj-methane-capture" />
            </Field>
          )}
          {pia?.project_type === "fuel_switch" && (
            <Field label="% of diesel/petrol switched to LPG" hint="Default: 40% of fleet switched. Remainder stays diesel.">
              <Input value={form.switch_fraction_pct} onChange={v => set("switch_fraction_pct", v)}
                type="number" placeholder="40" testid="proj-switch-fraction" />
            </Field>
          )}
          {pia?.project_type === "agriculture_land_use" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Land area (ha)">
                <Input value={form.land_area_ha} onChange={v => set("land_area_ha", v)} type="number" placeholder="0" testid="proj-land-ha" />
              </Field>
              <Field label="Sequestration (tCO₂e/ha/yr)" hint="Default: 0.5 (conservative). Range: 0.2–3.0.">
                <Input value={form.soil_sequestration_tco2e_per_ha} onChange={v => set("soil_sequestration_tco2e_per_ha", v)} type="number" placeholder="0.5" testid="proj-seq-per-ha" />
              </Field>
            </div>
          )}
          {(pia?.project_type === "energy_efficiency" || pia?.project_type === "industrial_process" || pia?.project_type === "other" || pia?.project_type === "circular_recycling" || !pia?.project_type) && (
            <Field label={typeCfg.rf_label} hint="Edit this value — do not leave as a default without documenting the basis.">
              <Input value={form.reduction_factor_pct} onChange={v => set("reduction_factor_pct", v)}
                type="number" placeholder={String(typeCfg.default_rf)} testid="proj-reduction-pct" />
            </Field>
          )}

          {/* Assumption documentation — mandatory for credibility */}
          <div className="mt-3">
            <Field label="Document your assumption (required for credibility)" hint="FI analysts and auditors will ask: 'How did you arrive at this %?'">
              <Input value={form.reduction_assumption_note}
                onChange={v => set("reduction_assumption_note", v)}
                placeholder="e.g. Based on technical study by [firm], or manufacturer spec for [equipment]"
                testid="proj-assumption-note" />
            </Field>
          </div>
        </div>

        {/* Secondary fields */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="New Technology / Intervention">
            <Input value={form.new_technology} onChange={v => set("new_technology", v)}
              placeholder="e.g. 100kW solar + 50kWh battery" testid="proj-tech" />
          </Field>
          <Field label="Ramp-up Period (years)" hint="Years to reach full operating capacity">
            <Input value={form.ramp_up_years || 1} onChange={v => set("ramp_up_years", v)}
              type="number" placeholder="1" testid="proj-rampup" />
          </Field>
        </div>
      </div>
    </StepCard>
  );
}

// ── STEP 4: Impact Over Time ────────────────────────────────────────────────
function Step4_Impact({ pia }) {
  if (!pia?.impact_timeline?.length) {
    return (
      <StepCard number={4} icon={BarChart2} title="Impact Over Time"
        description="Annual and cumulative emission reduction trajectory.">
        <div className="text-center py-8 text-xs text-slate-400">
          Complete Step 3 to generate the impact timeline.
        </div>
      </StepCard>
    );
  }

  const data = pia.impact_timeline;
  const totalYears = data.length;
  const lifetime   = pia.lifetime_reduction_tco2e ? Math.round(pia.lifetime_reduction_tco2e * 100) / 100 : null;
  const yr1        = data[0]?.annual_reduction || 0;

  return (
    <StepCard number={4} icon={BarChart2} title="Impact Over Time"
      description="Annual reduction trajectory and cumulative impact over project life.">
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: "Year 1 Reduction",  value: `${yr1}t`,      color: "text-teal-600" },
          { label: "Full-Rate Annual",  value: `${pia.emission_reduction_tco2e}t`, color: "text-slate-900" },
          { label: "Lifetime Total",    value: `${lifetime}t`, color: "text-emerald-600" },
        ].map(m => (
          <div key={m.label} className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200">
            <p className={`text-xl font-bold tabular-nums font-mono ${m.color}`}>{m.value}</p>
            <p className="text-xs text-slate-400 mt-1">tCO₂e</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">Annual Reduction Trajectory</p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94A3B8" }} label={{ value: "Year", position: "insideBottom", offset: -5, fill: "#94A3B8", fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={v => `${v}t`} />
            <Tooltip formatter={(v) => [`${v}t CO₂e`, "Annual Reduction"]} contentStyle={{ fontSize: 11 }} />
            <Bar dataKey="annual_reduction" fill={TEAL} radius={[2, 2, 0, 0]} name="Annual Reduction" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">Cumulative Impact</p>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94A3B8" }} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={v => `${v}t`} />
            <Tooltip formatter={(v) => [`${v}t CO₂e`, "Cumulative"]} contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="cumulative_reduction" stroke={TEAL} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </StepCard>
  );
}

// ── STEP 5: MRV Data Quality ────────────────────────────────────────────────
function Step5_MRV({ pia, onSaved }) {
  const [form, setForm] = useState(pia?.mrv_data || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/mrv`, form);
      toast.success(`MRV readiness: ${res.data.mrv_readiness}`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  const FREQ_OPTS = ["monthly","quarterly","annually","as_needed"].map(f => ({ value: f, label: f.charAt(0).toUpperCase() + f.slice(1) }));

  return (
    <StepCard number={5} icon={Shield} title="Data Quality & MRV Readiness"
      description="Can this impact be monitored and defended? MRV readiness is critical for carbon markets and institutional finance."
      footer={<>
        <SaveBtn onClick={handleSave} busy={busy} />
        {pia?.mrv_readiness && (
          <span className={`ml-4 text-xs font-bold px-3 py-1.5 rounded-lg capitalize ${SCORE_COLORS[pia.mrv_readiness]}`}>
            MRV: {pia.mrv_readiness}
          </span>
        )}
      </>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary Data Sources">
          <Input value={form.data_sources} onChange={v => set("data_sources", v)}
            placeholder="e.g. Utility bills, smart meters, on-site sensors" testid="mrv-sources" />
        </Field>
        <Field label="Measurement Frequency">
          <Select value={form.measurement_frequency} onChange={v => set("measurement_frequency", v)}
            options={FREQ_OPTS} testid="mrv-frequency" />
        </Field>
        <Field label="Responsible Owner / Monitor">
          <Input value={form.responsible_owner} onChange={v => set("responsible_owner", v)}
            placeholder="e.g. Operations Manager or External MRV Provider" testid="mrv-owner" />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={v => set("notes", v)}
            placeholder="Additional monitoring context" testid="mrv-notes" />
        </Field>
        <div className="flex flex-col gap-3">
          {[
            ["metering_available",     "Metering / monitoring equipment is in place"],
            ["evidence_uploaded",      "Supporting evidence documents uploaded"],
            ["monitoring_plan_exists", "Monitoring plan or MRV protocol exists"],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!form[k]}
                onChange={e => set(k, e.target.checked)}
                data-testid={`mrv-${k}`}
                className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
              <span className="text-slate-600">{l}</span>
            </label>
          ))}
        </div>
      </div>
    </StepCard>
  );
}

// ── STEP 6: Additionality & Integrity ──────────────────────────────────────
function Step6_Additionality({ pia, onSaved }) {
  const [form, setForm] = useState(pia?.additionality_data || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/additionality`, form);
      toast.success(`Additionality appears ${res.data.additionality_score}`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  const CHECKS = [
    { key: "regulatory_check",      label: "Regulatory Additionality",
      hint:    "Is this project required by law or regulation?",
      options: [{value:"not_required",label:"Not required by regulation"},{value:"partial",label:"Partially covered"},{value:"required",label:"Legally mandated"}] },
    { key: "financial_check",       label: "Financial Additionality",
      hint:    "Would this project proceed without carbon revenue?",
      options: [{value:"positive",label:"Carbon revenue is needed to proceed"},{value:"partial",label:"Enhances but not critical"},{value:"negative",label:"Proceeds regardless of carbon"}] },
    { key: "common_practice_check", label: "Common Practice",
      hint:    "Is this technology / approach standard in this market?",
      options: [{value:"not_common",label:"Not common practice in this market"},{value:"emerging",label:"Emerging — limited adoption"},{value:"common",label:"Already common practice"}] },
    { key: "policy_dependency",     label: "Policy Dependency",
      hint:    "Does this project depend on a specific policy or incentive?",
      options: [{value:"none",label:"Independent of policy"},{value:"partial",label:"Partially policy-supported"},{value:"dependent",label:"Fully policy-dependent"}] },
  ];

  return (
    <StepCard number={6} icon={CheckSquare} title="Additionality & Integrity Check"
      description="Test whether the project is truly climate-relevant. Language: 'appears strong/moderate/weak' — not 'eligible' or 'qualifies'."
      footer={<>
        <SaveBtn onClick={handleSave} busy={busy} />
        {pia?.additionality_score && (
          <span className={`ml-4 text-xs font-bold px-3 py-1.5 rounded-lg capitalize ${SCORE_COLORS[pia.additionality_score]}`}>
            Additionality appears: {pia.additionality_score}
          </span>
        )}
      </>}>
      <div className="space-y-5">
        {CHECKS.map(c => (
          <div key={c.key}>
            <Field label={c.label} hint={c.hint}>
              <Select value={form[c.key]} onChange={v => set(c.key, v)}
                options={c.options} testid={`add-${c.key}`} />
            </Field>
          </div>
        ))}
        <Field label="Notes">
          <textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)}
            placeholder="Supporting evidence for additionality claims..."
            rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-500" />
        </Field>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
          <Info size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-500">
            Additionality assessment is indicative based on current inputs. Final determination requires independent validation by an accredited third party. DeCarbonIQ does not certify additionality.
          </p>
        </div>
      </div>
    </StepCard>
  );
}

// ── STEP 7: Carbon Value & Pathway ─────────────────────────────────────────
// ── Housing Carbon Screening Panel (Downstream / Exploratory) ─────────────
function HousingCarbonScreeningPanel({ pia }) {
  const [screening, setScreening] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [units,     setUnits]     = useState(100);
  const [capex,     setCapex]     = useState(pia?.capex_usd || 0);
  const [finView,   setFinView]   = useState(true);  // default to Finance View

  // Extract per-unit metrics from project data if available
  const projectData = pia?.project_data || {};
  const embodiedRed  = parseFloat(projectData._dpcs_embodied_reduction || 0);
  const energySavings= parseFloat(projectData._dpcs_energy_cost_savings_yr || 0);
  const lifeYrs      = parseInt((pia?.baseline_data || {}).project_life_years || 30);

  const runScreening = async () => {
    setLoading(true);
    try {
      const r = await client.post("/housing/carbon-screening", {
        annual_reduction_per_unit:   pia.emission_reduction_tco2e || 0,
        units_count:                 units,
        capex_per_unit_usd:          capex,
        mrv_readiness:               pia.mrv_readiness || "weak",
        additionality_score:         pia.additionality_score || "moderate",
        country:                     pia.country || "Nigeria",
        embodied_reduction_per_unit: embodiedRed,
        life_years:                  lifeYrs,
        energy_savings_per_unit_yr:  energySavings,
      });
      setScreening(r.data);
    } catch { toast.error("Screening failed."); }
    setLoading(false);
  };

  const FI_COLORS = { ready: "text-emerald-700 bg-emerald-50 border-emerald-200", potential: "text-teal-700 bg-teal-50 border-teal-200", early_stage: "text-amber-700 bg-amber-50 border-amber-200" };
  const VCM_COLORS = { exploratory: "text-teal-700 bg-teal-50", not_viable_at_scale: "text-slate-500 bg-slate-50" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mt-3" data-testid="housing-carbon-screening">
      <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-slate-900">Carbon Market Screening (Downstream)</p>
          <p className="text-xs text-slate-500 mt-0.5">Exploratory — not an eligibility determination.</p>
        </div>
        {screening && (
          <button onClick={() => setFinView(v => !v)}
            className="text-xs font-bold px-2.5 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
            data-testid="screening-view-toggle">
            {finView ? "Technical View" : "Finance View"}
          </button>
        )}
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Programme Scale (units)</label>
            <input type="number" min="1" value={units} onChange={e => setUnits(parseInt(e.target.value)||1)}
              className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-teal-400"
              data-testid="screening-units-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Incremental CAPEX per Unit (USD)</label>
            <input type="number" min="0" value={capex} onChange={e => setCapex(parseFloat(e.target.value)||0)}
              className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-teal-400" />
          </div>
        </div>
        <button onClick={runScreening} disabled={loading || !pia?.emission_reduction_tco2e}
          className="flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-lg hover:bg-slate-800 disabled:opacity-40"
          data-testid="run-screening-btn">
          {loading ? <RefreshCw size={11} className="animate-spin" /> : <Target size={11} />}
          {loading ? "Screening..." : "Run Screening"}
        </button>

        {/* Download Finance Brief — the 30-second decision sheet */}
        {pia?.emission_reduction_tco2e > 0 && (
          <button
            onClick={async () => {
              try {
                const projectData = pia?.project_data || {};
                const token = localStorage.getItem("decarboniq_token");
                const res = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/housing/finance-brief`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                      project_name:                  pia.name || "Housing Project",
                      developer_name:                "Developer",
                      country:                       pia.country || "Nigeria",
                      floor_area_m2:                 parseFloat((pia.baseline_data||{}).floor_area_m2 || 0),
                      embodied_reduction_tco2e:      parseFloat(projectData._dpcs_embodied_reduction || 0),
                      operational_reduction_tco2e_yr:pia.emission_reduction_tco2e,
                      total_lifetime_reduction_tco2e:parseFloat(projectData._dpcs_total_lifetime_reduction || pia.lifetime_reduction_tco2e || 0),
                      project_life_years:            parseInt((pia.baseline_data||{}).project_life_years||30),
                      units_count:                   parseInt(units) || 100,
                      incremental_cost_usd:          parseFloat(projectData.incremental_cost_usd || 0),
                      cost_per_tco2e:                parseFloat(projectData._dpcs_cost_per_tco2e || 0) || null,
                      energy_savings_yr:             parseFloat(projectData._dpcs_energy_cost_savings_yr || 0),
                      benchmark_position:            "Run screening to compute",
                      mrv_readiness:                 pia.mrv_readiness || "weak",
                      operational_reduction_pct:     parseFloat((pia.emission_reduction_tco2e / (pia.baseline_emissions_tco2e || 1) * 100).toFixed(1)),
                      embodied_reduction_pct:        20,
                    }),
                  }
                );
                if (!res.ok) throw new Error("Brief generation failed");
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `FinanceBrief-${(pia.name||"Housing").replace(/\s+/g,"-")}.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Finance Brief downloaded.");
              } catch { toast.error("Brief generation failed."); }
            }}
            className="flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-700 text-xs font-bold rounded-lg hover:bg-teal-50 transition-colors"
            data-testid="download-finance-brief-btn">
            <FileText size={11} />
            Download Finance Brief
          </button>
        )}

        {screening && finView && (
          /* ── FINANCE VIEW ───────────────────────────────── */
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[
                ["Programme Annual Operational", `${screening.programme_annual_operational_tco2e_yr}t`, "tCO₂e/yr"],
                ["Upfront Embodied Reduction",   `${screening.programme_upfront_embodied_tco2e}t`, "tCO₂e (one-time)"],
                ["Total Lifetime Reduction",     `${(screening.programme_total_lifetime_tco2e/1000).toFixed(1)}k`, "tCO₂e"],
                ["Cost per tCO₂e",               screening.cost_per_tco2e ? `~$${Math.round(screening.cost_per_tco2e*0.85)}-${Math.round(screening.cost_per_tco2e*1.15)}/t*` : "—", "(indicative range)"],
              ].map(([l,v,sub]) => (
                <div key={l} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 leading-tight">{l}</p>
                  <p className="text-sm font-bold text-slate-900">{v}</p>
                  {sub && <p className="text-xs text-slate-400">{sub}</p>}
                </div>
              ))}
            </div>
            {screening.cost_per_tco2e && (
              <p className="text-xs text-slate-400 italic">{screening.cost_basis}</p>
            )}
            <div className={`rounded-xl border px-4 py-3 ${FI_COLORS[screening.climate_finance_readiness] || FI_COLORS.early_stage}`}>
              <p className="text-xs font-bold uppercase tracking-wide mb-1">Climate Finance Readiness</p>
              <p className="text-xs">{screening.climate_finance_note}</p>
            </div>

            {/* Benchmark comparison */}
            {screening.benchmarks && screening.cost_per_tco2e && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">DFI Benchmark Comparison</p>
                <div className="space-y-1.5">
                  {screening.benchmarks.map(b => {
                    const cost = screening.cost_per_tco2e;
                    const inRange = cost >= b.range_low && cost <= b.range_high;
                    const below = cost < b.range_low;
                    return (
                      <div key={b.name} className={`flex items-center justify-between rounded-lg px-3 py-1.5 ${inRange ? "bg-teal-50 border border-teal-200" : below ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-slate-100"}`}
                        data-testid={`benchmark-${b.name.replace(/\s+/g,"-").toLowerCase()}`}>
                        <div>
                          <p className="text-xs font-bold text-slate-700">{b.name}</p>
                          <p className="text-xs text-slate-400">{b.source}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-xs font-bold text-slate-600">${b.range_low}–${b.range_high}/t</p>
                          <p className={`text-xs font-bold ${inRange ? "text-teal-600" : below ? "text-emerald-600" : "text-slate-400"}`}>
                            {inRange ? "In range" : below ? "Below (competitive)" : "Above range"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-1.5 italic">Reference ranges only. Not official financing eligibility criteria.</p>
              </div>
            )}

            {/* Finance narrative */}
            {screening.finance_narrative && (
              <div className="bg-[#0F172A] rounded-xl px-4 py-3">
                <p className="text-xs text-teal-400 font-bold uppercase tracking-wide mb-1.5">Finance Narrative</p>
                <p className="text-xs text-slate-300 leading-relaxed">{screening.finance_narrative}</p>
              </div>
            )}
            <div className={`rounded-xl px-4 py-3 ${VCM_COLORS[screening.vcm_potential] || VCM_COLORS.not_viable_at_scale}`}>
              <p className="text-xs font-bold uppercase tracking-wide mb-1">VCM Potential (Exploratory)</p>
              <p className="text-xs text-slate-600">{screening.vcm_note}</p>
            </div>
            <div className="bg-amber-50 rounded-xl px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1">Article 6 (Indicative Future)</p>
              <p className="text-xs text-amber-600">{screening.article6_note?.substring(0,200)}...</p>
            </div>
            <p className="text-xs text-slate-400 italic">{screening.disclaimer}</p>
          </div>
        )}

        {screening && !finView && (
          /* ── TECHNICAL VIEW ────────────────────────────── */
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-center">
              {[
                ["Annual Op. Reduction",    `${screening.programme_annual_operational_tco2e_yr}t/yr`],
                ["Upfront Embodied",         `${screening.programme_upfront_embodied_tco2e}t`],
                ["Lifetime Op.",             `${screening.programme_lifetime_operational_tco2e}t`],
                ["Total Lifetime",           `${screening.programme_total_lifetime_tco2e}t`],
              ].map(([l,v]) => (
                <div key={l} className="bg-slate-50 rounded-xl p-2.5">
                  <p className="text-xs text-slate-400">{l}</p>
                  <p className="text-xs font-bold text-slate-900">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 italic">{screening.disclaimer}</p>
          </div>
        )}
      </div>
    </div>
  );
}


function Step7_Pathway({ pia }) {
  const [pathway, setPathway] = useState(null);
  const [sdgData, setSdgData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPathway = async () => {
    if (!pia?.id || !pia?.emission_reduction_tco2e) return;
    setLoading(true);
    try {
      const [pathRes, sdgRes] = await Promise.allSettled([
        client.get(`/project-assessment/${pia.id}/pathway`),
        client.get(`/project-assessment/${pia.id}/sdg`),
      ]);
      if (pathRes.status === "fulfilled") setPathway(pathRes.value.data);
      if (sdgRes.status  === "fulfilled") setSdgData(sdgRes.value.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadPathway(); }, [pia?.additionality_score, pia?.mrv_readiness]);

  if (!pia?.emission_reduction_tco2e) {
    return (
      <StepCard number={7} icon={GitBranch} title="Carbon Value & Pathway"
        description="Translate impact into climate asset potential.">
        <div className="text-center py-6 text-xs text-slate-400">Complete Steps 2–3 to unlock pathway routing.</div>
      </StepCard>
    );
  }

  const reduction = pia.emission_reduction_tco2e;
  const lifetime  = pia.lifetime_reduction_tco2e;
  const vcmLow    = Math.round(reduction * 15);
  const vcmHigh   = Math.round(reduction * 30);

  const PATHWAY_STYLE = {
    "Suitable":                     { bg: "bg-emerald-50 border-emerald-200", badge: "bg-emerald-100 text-emerald-700" },
    "Potential — subject to validation": { bg: "bg-teal-50 border-teal-200",   badge: "bg-teal-100 text-teal-700" },
    "Not yet suitable":             { bg: "bg-slate-50 border-slate-200",   badge: "bg-slate-100 text-slate-500" },
    "Indicative potential — sovereign process required": { bg: "bg-amber-50 border-amber-200", badge: "bg-amber-100 text-amber-700" },
    "Not recommended at this stage":{ bg: "bg-slate-50 border-slate-200",   badge: "bg-slate-100 text-slate-400" },
  };

  return (
    <StepCard number={7} icon={GitBranch} title="Carbon Value & Pathway"
      description="Pathway routing derived from project characteristics. All language is indicative — not an eligibility determination.">
      {/* Carbon value summary */}
      <div className="bg-[#0F172A] rounded-xl p-5 mb-5 text-white">
        <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Carbon Value Potential (indicative)</p>
        <div className="grid grid-cols-3 gap-4">
          <div><p className="text-2xl font-bold text-teal-400 font-mono">{reduction}t</p><p className="text-xs text-slate-400">tCO₂e/yr</p></div>
          <div><p className="text-2xl font-bold text-white font-mono">{lifetime}t</p><p className="text-xs text-slate-400">Lifetime tCO₂e</p></div>
          <div><p className="text-xl font-bold text-emerald-400">${(vcmLow/1000).toFixed(0)}k–${(vcmHigh/1000).toFixed(0)}k</p><p className="text-xs text-slate-400">at $15–30/tCO₂e</p></div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-4">
          <RefreshCw size={12} className="animate-spin" /> Computing pathway options...
        </div>
      ) : pathway?.pathways?.map(p => {
        const style = PATHWAY_STYLE[p.label] || PATHWAY_STYLE["Not yet suitable"];
        return (
          <div key={p.id} className={`rounded-xl border p-4 mb-3 ${style.bg}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-sm font-bold text-slate-900">{p.name}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${style.badge}`}>{p.label}</span>
            </div>
            <p className="text-xs text-slate-500 mb-2">{p.why}</p>
            {p.speed && <p className="text-xs text-slate-400 mb-1">Timeline: {p.speed}</p>}
            {p.disclaimer && (
              <p className="text-xs text-slate-400 italic mt-2 border-t border-slate-200 pt-2">{p.disclaimer}</p>
            )}
          </div>
        );
      })}

      {pathway?.platform_disclaimer && (
        <p className="text-xs text-slate-400 italic mt-2">{pathway.platform_disclaimer}</p>
      )}

      {/* SDG Attribution — traceable, rule-based */}
      {sdgData && (
        <SectionCard>

          <SDGFullPanel sdgData={sdgData} compact={true} />
        
        </SectionCard>
      )}

      {/* Housing: downstream carbon screening panel */}
      {pia?.project_type === "housing_low_carbon" && (
        <HousingCarbonScreeningPanel pia={pia} />
      )}
    </StepCard>
  );
}

// ── STEP 8: Investment & Financial Case ────────────────────────────────────
function Step8_Financial({ pia, onSaved }) {
  const [form, setForm] = useState(pia?.financial_data || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/financial`, {
        ...form,
        capex_usd:          parseFloat(form.capex_usd)          || null,
        opex_change_usd:    parseFloat(form.opex_change_usd)    || null,
        annual_savings_usd: parseFloat(form.annual_savings_usd) || null,
        revenue_uplift_usd: parseFloat(form.revenue_uplift_usd) || null,
      });
      toast.success(`Payback: ${res.data.payback_years || "—"} yrs · Cost/tCO₂e: $${res.data.cost_per_tco2e || "—"}`);
      onSaved(res.data);
    } catch { toast.error("Save failed."); }
    setBusy(false);
  };

  return (
    <StepCard number={8} icon={DollarSign} title="Investment & Financial Case"
      description="Structure the project for financing. CAPEX, savings, payback, and cost per tCO₂e reduced."
      footer={<>
        <SaveBtn onClick={handleSave} busy={busy} />
        {pia?.payback_years && (
          <div className="ml-4 bg-slate-100 rounded-lg px-4 py-2 flex items-center gap-4">
            <span className="text-xs text-slate-500">Payback:</span>
            <span className="text-sm font-bold font-mono text-slate-900">{pia.payback_years} yrs</span>
            {pia.cost_per_tco2e && <><span className="text-xs text-slate-500">Cost/tCO₂e:</span><span className="text-sm font-bold font-mono text-slate-900">${pia.cost_per_tco2e}</span></>}
          </div>
        )}
      </>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Total CAPEX (USD)" hint="Total investment required">
          <Input value={form.capex_usd} onChange={v => set("capex_usd", v)} type="number" placeholder="0" testid="fin-capex" />
        </Field>
        <Field label="Annual Savings (USD/yr)" hint="Fuel/energy savings from project">
          <Input value={form.annual_savings_usd} onChange={v => set("annual_savings_usd", v)} type="number" placeholder="0" testid="fin-savings" />
        </Field>
        <Field label="Revenue Uplift (USD/yr)" hint="Carbon credits or other revenue (optional)">
          <Input value={form.revenue_uplift_usd} onChange={v => set("revenue_uplift_usd", v)} type="number" placeholder="0" testid="fin-revenue" />
        </Field>
        <Field label="OPEX Change (USD/yr)" hint="Increase in annual operating costs (positive = higher costs)">
          <Input value={form.opex_change_usd} onChange={v => set("opex_change_usd", v)} type="number" placeholder="0" testid="fin-opex" />
        </Field>
        <div className="col-span-2">
          <Field label="Financial Notes">
            <textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)}
              placeholder="Financial assumptions, sources, and context..."
              rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-500" />
          </Field>
        </div>
      </div>
    </StepCard>
  );
}

// ── STEP 9: Readiness Decision ─────────────────────────────────────────────
function Step9_Readiness({ pia }) {
  const ready = READINESS_CFG[pia?.readiness_state] || READINESS_CFG.not_ready;

  const STEPS_STATUS = [
    { label: "Project defined",       done: !!pia?.project_type },
    { label: "Baseline computed",     done: !!pia?.baseline_emissions_tco2e },
    { label: "Project scenario set",  done: !!pia?.emission_reduction_tco2e },
    { label: "MRV assessed",          done: !!pia?.mrv_readiness },
    { label: "Additionality checked", done: !!pia?.additionality_score },
    { label: "Financial case built",  done: !!pia?.capex_usd },
  ];
  const doneCount  = STEPS_STATUS.filter(s => s.done).length;
  const readyPct   = Math.round(doneCount / STEPS_STATUS.length * 100);

  return (
    <StepCard number={9} icon={Target} title="Readiness Decision"
      description="Overall project readiness across all assessment dimensions.">
      {/* Main readiness indicator */}
      <div className={`rounded-xl border-2 p-6 mb-5 ${ready.bg}`}>
        <div className="flex items-center gap-3 mb-2">
          <span className={`w-3 h-3 rounded-full ${ready.dot}`} />
          <p className={`text-lg font-bold ${ready.color}`}>{ready.label}</p>
        </div>
        <p className="text-sm text-slate-500">
          {pia?.readiness_state === "not_ready"           && "Complete Steps 2–4 to progress beyond 'Not Ready'."}
          {pia?.readiness_state === "structuring_needed"  && "Strengthen MRV and additionality before progressing."}
          {pia?.readiness_state === "ready_for_financing" && "Project has a defensible financial case. Ready for financing review."}
          {pia?.readiness_state === "ready_for_project"   && "Project structure is sound. Ready to proceed to formal project development."}
          {pia?.readiness_state === "ready_for_pathway"   && "Strong MRV and additionality. May be suitable for carbon pathway exploration."}
        </p>
      </div>

      {/* Progress checklist */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-xs mb-2">
          <p className="font-semibold text-slate-600">Assessment Progress</p>
          <p className="text-slate-400">{doneCount}/{STEPS_STATUS.length} complete</p>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
          <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${readyPct}%` }} />
        </div>
        <div className="space-y-2">
          {STEPS_STATUS.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {s.done
                ? <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
              }
              <span className={s.done ? "text-slate-600" : "text-slate-400"}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </StepCard>
  );
}

// ── STEP 10: Outputs & Next Actions ────────────────────────────────────────
function Step10_Outputs({ pia, navigate, onPiaUpdate }) {
  const [submitting,  setSubmitting]  = useState(false);
  const [finRequest,  setFinRequest]  = useState(null);
  const [purpose,     setPurpose]     = useState("");
  const [freezing,    setFreezing]    = useState(false);
  const [freezeNotes, setFreezeNotes] = useState("");
  const [showFreeze,  setShowFreeze]  = useState(false);

  const isFrozen = pia?.is_frozen;

  const handleDownload = async () => {
    if (!pia?.id) { toast.error("Complete the assessment first."); return; }
    try {
      const token = localStorage.getItem("decarboniq_token");
      const url   = `${process.env.REACT_APP_BACKEND_URL}/api/export-pdf/project/${pia.id}`;
      const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF failed");
      const blob  = await res.blob();
      const a     = document.createElement("a");
      a.href      = URL.createObjectURL(blob);
      a.download  = `DCI-ProjectImpactReport-${pia.ref_id || pia.id}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Project Impact Report downloaded.");
      // Track activation event (fire-and-forget)
      import("@/api/client").then(m => m.default.post("/activation/track/report_generated").catch(() => {}));
    } catch { toast.error("PDF generation failed."); }
  };

  const handleFreeze = async () => {
    if (!pia?.emission_reduction_tco2e) {
      toast.error("Cannot freeze: complete all calculation steps first.");
      return;
    }
    setFreezing(true);
    try {
      await client.post(`/project-assessment/${pia.id}/freeze`, { notes: freezeNotes || null });
      toast.success("Assessment frozen — inputs and outputs are now locked for audit.");
      setShowFreeze(false);
      if (onPiaUpdate) onPiaUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freeze failed.");
    }
    setFreezing(false);
  };

  const handleSubmitForFinancing = async () => {
    if (!pia?.baseline_emissions_tco2e) {
      toast.error("Complete baseline and project scenario before submitting for financing.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await client.post(`/project-assessment/${pia.id}/submit-for-financing`, {
        request_type: "capex_support",
        amount_requested_usd: pia.capex_usd,
        purpose: purpose || `Climate project: ${pia.name}`,
      });
      setFinRequest(res.data.request_id);
      toast.success(`Project submitted to FI queue: ${res.data.request_id}`);
    } catch { toast.error("Submission failed."); }
    setSubmitting(false);
  };

  return (
    <StepCard number={10} icon={FileText} title="Outputs & Next Actions"
      description="Move the project into the next system.">

      {/* ── GAP 2: Freeze Assessment ───────────────────────────────────────── */}
      {isFrozen ? (
        <div className="flex items-start gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl mb-5"
          data-testid="freeze-status-locked">
          <Shield size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-emerald-800">Assessment Frozen — Audit-Ready</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              All inputs, outputs, and SDG attribution are locked. This is a fixed, auditable snapshot.
              {pia.frozen_at && ` Frozen at: ${new Date(pia.frozen_at).toLocaleString()}`}
            </p>
            {pia.frozen_notes && (
              <p className="text-xs text-emerald-600 italic mt-0.5">Notes: {pia.frozen_notes}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden mb-5">
          <button onClick={() => setShowFreeze(!showFreeze)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            data-testid="freeze-toggle-btn">
            <div className="flex items-center gap-2">
              <Shield size={12} className="text-slate-500" />
              <span className="text-xs font-bold text-slate-700">Freeze Assessment for Submission</span>
              <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">Governance</span>
            </div>
            <ChevronRight size={12} className={`text-slate-400 transition-transform ${showFreeze ? "rotate-90" : ""}`} />
          </button>
          {showFreeze && (
            <div className="px-4 py-3 bg-white space-y-3">
              <p className="text-xs text-slate-600">
                Freezing creates a fixed, auditable snapshot. Inputs, calculations, and SDG attribution will be
                locked. Required before formal FI submission or third-party verification.
              </p>
              <div className="space-y-1">
                {[
                  "Calculation outputs (baseline, reduction, lifetime)",
                  "SDG attribution and confidence levels",
                  "MRV readiness and additionality scores",
                  "Financial case (CAPEX, payback, cost/tCO₂e)",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="text-teal-500 font-bold">+</span> {item} will be locked
                  </div>
                ))}
              </div>
              <textarea value={freezeNotes} onChange={e => setFreezeNotes(e.target.value)}
                placeholder="Optional note for audit trail (e.g. 'Submitted to Keystone Capital for review')"
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:border-teal-400" />
              <button onClick={handleFreeze} disabled={freezing || !pia?.emission_reduction_tco2e}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-lg hover:bg-slate-800 disabled:opacity-40"
                data-testid="confirm-freeze-btn">
                <Shield size={12} />
                {freezing ? "Freezing..." : "Freeze Assessment"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Project → FI Financing submission */}
      <div className="bg-[#0F172A] rounded-xl p-5 mb-5 text-white">
        <p className="text-xs text-teal-400 uppercase tracking-widest font-bold mb-2">Submit for Financing Review</p>
        <p className="text-xs text-slate-300 mb-4">
          Send this project assessment directly to the FI pipeline. The assessment data is frozen at submission.
        </p>
        {finRequest ? (
          <div className="flex items-center gap-3">
            <CheckCircle2 size={16} className="text-emerald-400" />
            <div>
              <p className="text-sm font-bold text-white">Submitted: <span className="font-mono">{finRequest}</span></p>
              <button onClick={() => navigate("/fi/requests")} className="text-xs text-teal-400 hover:underline">View in FI queue →</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <textarea value={purpose} onChange={e => setPurpose(e.target.value)}
              placeholder={`Financing purpose (e.g. "Fund ${pia?.name} capital expenditure of $${(pia?.capex_usd || 0).toLocaleString()}")`}
              rows={2} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-500 resize-none focus:outline-none focus:border-teal-500" />
            <button onClick={handleSubmitForFinancing} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-500 disabled:opacity-40"
              data-testid="submit-for-fi-btn">
              {submitting ? <RefreshCw size={12} className="animate-spin" /> : <ArrowRight size={12} />}
              {submitting ? "Submitting..." : "Submit to FI Pipeline"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        {[
          { label: "Project Impact Report",  desc: "Baseline, reduction, MRV, additionality, financial case, pathway — investor-grade",  icon: BarChart2,  primary: true  },
          { label: "Financial Summary",      desc: "CAPEX, payback, cost per tCO₂e, savings",  icon: DollarSign,  primary: false },
          { label: "MRV Summary",            desc: "Data quality and monitoring readiness",     icon: Shield,      primary: false },
          { label: "Carbon Pathway Brief",   desc: "Pathway options and indicative value",      icon: GitBranch,   primary: false },
        ].map(r => {
          const Icon = r.icon;
          return (
            <div key={r.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <Icon size={13} className={r.primary ? "text-teal-600" : "text-slate-400"} />
                  <p className={`text-xs font-bold ${r.primary ? "text-slate-900" : "text-slate-600"}`}>{r.label}</p>
                  {r.primary && <span className="text-xs font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">Full PDF</span>}
                </div>
                <p className="text-xs text-slate-400">{r.desc}</p>
              </div>
              <button
                onClick={r.primary ? handleDownload : () => handleDownload()}
                className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-xl ${
                  r.primary ? "bg-teal-600 text-white hover:bg-teal-700" : "border border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
                data-testid={`download-report-${r.label.replace(/\s+/g,"-").toLowerCase()}`}>
                <Download size={12} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Next Actions</p>
        {[
          // Route housing to the dedicated housing workspace
          ...(pia?.project_type === "housing_low_carbon"
            ? [{ label: "Open Housing Workspace",      path: "/housing-project",          icon: Layers }]
            : []),
          // Route biogas/solar to generic sector workspace
          ...((pia?.project_type === "waste_recovery" || pia?.project_type === "solar")
            ? [{ label: "Open Sector Workspace",        path: `/project/${pia.id}/workspace`, icon: Layers }]
            : []),
          { label: "Open as Structured Project",  path: "/cai",          icon: Layers    },
          { label: "Submit for Financing Review", path: "/fi/requests",   icon: DollarSign },
          { label: "View in Project Workspace",   path: "/assessments",   icon: FileText   },
        ].map(a => {
          const Icon = a.icon;
          return (
            <button key={a.label} onClick={() => navigate(a.path)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 hover:bg-slate-50 hover:border-teal-300 transition-all text-left">
              <Icon size={14} className="text-teal-600 flex-shrink-0" />
              {a.label}
              <ChevronRight size={12} className="text-slate-300 ml-auto" />
            </button>
          );
        })}
      </div>
    </StepCard>
  );
}

// ── TOP BAR ────────────────────────────────────────────────────────────────
function TopBar({ pia, onBack }) {
  const ready = READINESS_CFG[pia?.readiness_state] || READINESS_CFG.not_ready;
  return (
    <div className="h-14 bg-[#0B1220] border-b border-white/8 flex items-center px-5 gap-4 sticky top-0 z-30 flex-shrink-0"
      data-testid="project-top-bar">
      <button onClick={onBack} className="text-slate-500 hover:text-white transition-colors text-xs">
        ← Projects
      </button>
      <div className="w-px h-4 bg-white/10" />
      <div className="flex items-center gap-2">
        <Layers size={13} className="text-teal-400" />
        <p className="text-sm font-bold text-white truncate max-w-xs">
          {pia?.name || "New Project Assessment"}
        </p>
        {pia?.ref_id && <span className="text-xs text-slate-500 font-mono">{pia.ref_id}</span>}
      </div>
      <div className="flex-1" />
      {pia?.readiness_state && (
        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${ready.bg} ${ready.color}`}>
          {ready.label}
        </span>
      )}
      {pia?.is_frozen && (
        <span className="flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full border border-emerald-400/40 bg-emerald-900/30 text-emerald-400"
          data-testid="frozen-badge">
          <Shield size={10} /> FROZEN
        </span>
      )}
      <button
        onClick={async () => {
          if (!pia?.id) return;
          try {
            const token = localStorage.getItem("decarboniq_token");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/export-pdf/project/${pia.id}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `DCI-ProjectImpactReport-${pia.ref_id || pia.id}.pdf`;
            a.click();
            URL.revokeObjectURL(a.href);
          } catch {}
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-xs text-slate-300 rounded-lg hover:bg-white/5"
        data-testid="top-bar-pdf-btn">
        <Download size={12} /> Export PDF
      </button>
    </div>
  );
}

// ── PROJECT ASSESSMENT LIST ────────────────────────────────────────────────
function ProjectList({ navigate }) {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    client.get("/project-assessment/")
      .then(r => setList(r.data?.assessments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.warning("Enter a project name."); return; }
    setCreating(true);
    try {
      const res = await client.post("/project-assessment/", { name: newName.trim() });
      navigate(`/project/assessment/${res.data.id}`);
    } catch { toast.error("Could not create project."); }
    setCreating(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="project-list">
        <div className="flex items-start justify-between mb-8">
          <div>
            
      <PageHeader
        label="Assessment Steps"
        title="Project Impact Assessments"
        subtitle="Turn a project into a quantified, credible, and financeable climate asset."
        backUrl="/fi/requests"
        backLabel="Projects"
      />
        </div>
      </div>

        {/* New project form */}
        <SectionCard>

          <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="New project name (e.g. Ikeja Solar Hybrid)"
            className="flex-1 h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
            data-testid="new-pia-name"
            onKeyDown={e => e.key === "Enter" && handleCreate()} />
          <button onClick={handleCreate} disabled={creating}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
            data-testid="create-pia-btn">
            {creating ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
            Start Assessment
          </button>
        
        </SectionCard>

        {/* Existing assessments */}
        {list.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Layers size={32} className="mx-auto mb-3 text-slate-200" />
            <p className="text-sm font-semibold">No project assessments yet</p>
            <p className="text-xs mt-1">Create your first assessment above</p>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map(p => {
              const ready = READINESS_CFG[p.readiness_state] || READINESS_CFG.not_ready;
              return (
                <div key={p.id}
                  className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:border-teal-200 transition-all"
                  onClick={() => navigate(`/project/assessment/${p.id}`)}
                  data-testid={`pia-row-${p.id}`}>
                  <Layers size={16} className="text-teal-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-bold text-slate-900 truncate">{p.name}</p>
                      <span className="text-xs font-mono text-slate-400">{p.ref_id}</span>
                    </div>
                    <p className="text-xs text-slate-400">{p.project_type?.replace(/_/g," ")} · {p.country || "—"}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {p.annual_reduction && <span className="text-xs font-mono text-teal-600">{p.annual_reduction}t/yr</span>}
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${ready.bg} ${ready.color}`}>{ready.label}</span>
                    <ChevronRight size={13} className="text-slate-300" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function ProjectAssessmentPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();

  const [pia,         setPia]         = useState(null);
  const [summary,     setSummary]     = useState(null);
  const [activeStep,  setActiveStep]  = useState("setup");
  const [loading,     setLoading]     = useState(!!id);
  const [sdgData,     setSdgData]     = useState(null);

  const loadPia = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [piaRes, sumRes] = await Promise.all([
        client.get(`/project-assessment/${id}`),
        client.get(`/project-assessment/${id}/summary`),
      ]);
      setPia(piaRes.data);
      setSummary(sumRes.data);
      // Load SDG attribution if project has reduction data
      if (piaRes.data?.emission_reduction_tco2e) {
        client.get(`/project-assessment/${id}/sdg`).then(r => setSdgData(r.data)).catch(() => {});
      }
    } catch { toast.error("Could not load assessment."); }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadPia(); }, [loadPia]);

  const handleSaved = async (updatedPia) => {
    setPia(updatedPia);
    try {
      const sumRes = await client.get(`/project-assessment/${updatedPia.id}/summary`);
      setSummary(sumRes.data);
    } catch {}
    // Navigate to next step
    const currentIdx = STEPS.findIndex(s => s.key === activeStep);
    if (currentIdx < STEPS.length - 1) {
      setActiveStep(STEPS[currentIdx + 1].key);
    }
    if (!id) {
      navigate(`/project/assessment/${updatedPia.id}`, { replace: true });
    }
  };

  // Show list if no id
  if (!id) return <ProjectList navigate={navigate} />;

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <RefreshCw size={20} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="flex flex-col h-screen" data-testid="project-assessment-page">
      <TopBar pia={pia} onBack={() => navigate("/project/assessment")} />
      <div className="flex flex-1 min-h-0">
        <StepRail activeStep={activeStep} summary={summary} onStepClick={setActiveStep} />

        {/* Main canvas */}
        <div className="flex-1 overflow-y-auto bg-[#F8FAFC] px-6 py-6">
            {activeStep === "setup"         && <Step1_Setup         pia={pia}  onSaved={handleSaved} />}
            {activeStep === "baseline"      && <Step2_Baseline      pia={pia}  onSaved={handleSaved} />}
            {activeStep === "project"       && <Step3_Project       pia={pia}  onSaved={handleSaved} />}
            {activeStep === "impact"        && <Step4_Impact        pia={pia}  />}
            {activeStep === "mrv"           && <Step5_MRV           pia={pia}  onSaved={handleSaved} />}
            {activeStep === "additionality" && <Step6_Additionality pia={pia}  onSaved={handleSaved} />}
            {activeStep === "pathway"       && <Step7_Pathway       pia={pia}  />}
            {activeStep === "financial"     && <Step8_Financial     pia={pia}  onSaved={handleSaved} />}
            {activeStep === "readiness"     && <Step9_Readiness     pia={pia}  />}
            {activeStep === "outputs"       && <Step10_Outputs      pia={pia}  navigate={navigate} onPiaUpdate={loadPia} />}
          </div>
        </div>

        <InsightPanel summary={summary} sdgData={sdgData} />
    </div>
  );
}
